import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  filterContentForAbsoluteLibraryUrls,
  getChapters,
  getResources,
  getSingleGuidebookChapter,
} from '../../utils/utils';
import Page from '../Page';
import Loading from '../Loading';
import { downloadGuidebookEpub } from '../../utils/epubGeneration';
import { downloadGuidebookPdf } from '../../utils/pdfGeneration';
import { ArrowRight, Download, ArrowLeft } from 'lucide-react';
import { decodeHtml } from './FeaturedGuidebooks';

let GuidebookChapter = () => {
  let params = useParams();
  let [chapter, setChapter] = useState(null);
  let [content, setContent] = useState('');
  let [title, setTitle] = useState('');
  let [firstChapterTitle, setFirstChapterTitle] = useState('');
  let [initialTitle] = useState(document.title);
  let [isLoading, setIsLoading] = useState(true);

  // 1. Set the title of the page to the title of the chapter.
  useEffect(() => {
    // Get the current title so we can prepend the chapter title to it.
    document.title = `${decodeHtml(title)} - ${initialTitle}`;
  }, [title]);

  useEffect(() => {
    // 1. If there's a chapter slug, fetch the chapter content.
    if (params.chapterSlug) {
      async function fetch() {
        let chapter = await getSingleGuidebookChapter(params.chapterSlug);
        if (chapter.length > 0) {
          setChapter(chapter[0]);
        } else {
          setIsLoading(false);
        }
      }
      fetch();
    } else {
      // 2. If there's no chapter slug, fetch only the abstract (the first post in the guidebook taxonomy).
      async function fetch() {
        let chapters = await getChapters(params.guidebookSlug);
        // 2a. set the content of the first chapter to the content state.
        if (chapters.length > 0) {
          setChapter(chapters[0]);
        } else {
          setIsLoading(false);
        }
      }
      fetch();
    }
  }, [params.guidebookSlug, params.chapterSlug]);

  let [chapters, setChapters] = useState([]);
  useEffect(() => {
    async function fetch() {
      let chapters = await getChapters(params.guidebookSlug);
      // pop the first chapter off the array, since we're already displaying it.
      if (chapters.length > 0) {
        let firstChapter = chapters.shift();
        setFirstChapterTitle(firstChapter.title.rendered);
        setChapters(chapters);
      }
    }
    fetch();
  }, [params.guidebookSlug]);

  useEffect(() => {
    if (chapter) {
      setTitle(chapter.title.rendered);
      let filteredContent = filterContentForAbsoluteLibraryUrls(
        chapter.content.rendered
      );
      setContent(filteredContent);

      setIsLoading(false);
    }
  }, [chapter, isLoading]);

  useEffect(() => {
    if (!chapter && !isLoading) {
      setTitle("This chapter wasn't found");
      setContent(
        "This chapter wasn't found for this guidebook, please try another guidebook from the library."
      );
    }
  }, [chapter, isLoading]);

  return (
    <Page>
      {isLoading && <Loading />}
      <GuidebookChapterBreadcrumbs
        firstChapterTitle={firstChapterTitle}
        slug={params.guidebookSlug}
      />
      <div className="grid w-full grid-cols-3 gap-8">
        <div className="col-span-3 lg:col-span-2">
          <h1 className="guidebook-title">{decodeHtml(title)}</h1>
          <div
            className="guidebook-content"
            dangerouslySetInnerHTML={{ __html: content }}
          />
          {chapter && <AdditionalResources chapter={chapter} />}
          <NextPrevious />
        </div>
        <div
          id="chapter-list-container"
          className="col-span-3 mt-6 lg:col-span-1 lg:mt-0"
        >
          <ChapterList isLoading={isLoading} chapters={chapters} />
        </div>
      </div>
    </Page>
  );
};

let ChapterList = ({ chapters, isLoading }) => {
  let params = useParams();

  return (
    <div className="guidebook-sidebar flex flex-col bg-[var(--primary)] text-white font-bold border-gray-100 !border mt-6">
      {/* <div className="guidebook-sidebar flex flex-col bg-[#c3020a] text-white border-gray-100 border mt-6"> */}
      <div className="flex items-center justify-center py-0.5 bg-white px-4">
        <Link
          className="guidebook-toc-about uppercase"
          reloadDocument
          to={`/library/guidebooks/${params.guidebookSlug}`}
        >
          <span>About This Guidebook</span>
        </Link>
      </div>
      {isLoading && <Loading />}
      <ol className="guidebook-toc py-3 px-6 marker:font-bold">
        {!isLoading && !chapters.length && (
          <p>No chapters found for this guidebook.</p>
        )}
        {chapters.map((chapter, i) => {
          return (
            <li
              className="cursor-pointer text-white py-2 my-1"
              key={chapter.id}
            >
              <Link
                key={i}
                tabIndex={0}
                reloadDocument
                className="text-white"
                to={`/library/guidebooks/${params.guidebookSlug}/${chapter.slug}`}
              >
                {decodeHtml(chapter.title.rendered)}
              </Link>
            </li>
          );
        })}
      </ol>
      <div className="download-links px-4">
        <DownloadButtons />
      </div>
    </div>
  );
};

let NextPrevious = () => {
  let params = useParams();
  let [nextChapter, setNextChapter] = useState(null);
  let [previousChapter, setPreviousChapter] = useState(null);
  let [chapters, setChapters] = useState(null);

  useEffect(() => {
    async function fetch() {
      let chapters = await getChapters(params.guidebookSlug);
      setChapters(chapters);
      let currentChapter = chapters.findIndex(
        (chapter) => chapter.slug === params.chapterSlug
      );

      // If we're on the abstract, because chapterSlug will be null.
      if (currentChapter == -1) {
        currentChapter = 0;
      }

      if (currentChapter > 0) {
        setPreviousChapter(chapters[currentChapter - 1]);
      }
      if (currentChapter == 0) {
        setPreviousChapter(null);
      }
      if (currentChapter <= chapters.length - 1) {
        // If the next element exists, set the next chapter.
        let nextChapter = chapters[currentChapter + 1];
        if (nextChapter !== undefined) {
          setNextChapter(chapters[currentChapter + 1]);
        } else {
          setNextChapter(null);
        }
      }
    }
    fetch();
  }, [params.guidebookSlug, params.chapterSlug]);

  return (
    <div className="grid grid-cols-2 guidebook-chapter-nav print:hidden mt-10">
      <div className="col-span-1 guidebook-chapter prev">
        {previousChapter && (
          // If the previousChapter is the first chapter in the chapters array, link to the slugless guidebook page.
          <div className="col-span-1 guidebook-chapter next flex justify-start text-white">
            <Link
              tabIndex={0}
              reloadDocument
              className="flex items-center justify-center text-white uw-button"
              to={
                previousChapter.slug === chapters[0].slug
                  ? `/library/guidebooks/${params.guidebookSlug}`
                  : `/library/guidebooks/${params.guidebookSlug}/${previousChapter.slug}`
              }
            >
              <ArrowLeft className="text-white h-5 w-5 mr-2" />
              <p>{decodeHtml(previousChapter.title.rendered)}</p>
            </Link>
          </div>
        )}
      </div>
      <div className="col-span-1 guidebook-chapter next flex justify-end text-white">
        {nextChapter && (
          <Link
            tabIndex={0}
            className="uw-button flex items-center justify-center"
            reloadDocument
            to={`/library/guidebooks/${params.guidebookSlug}/${nextChapter.slug}`}
          >
            <p>{decodeHtml(nextChapter.title.rendered)}</p>
            <ArrowRight className="text-white h-5 w-5 ml-2" />
          </Link>
        )}
      </div>
    </div>
  );
};

let AdditionalResources = ({ chapter }) => {
  let [resources, setResources] = useState([]);
  useEffect(() => {
    async function fetch() {
      if (chapter.acf.additional_resources) {
        let resources = await getResources(chapter.acf.additional_resources);
        setResources(resources);
      }
    }
    fetch();
  }, [chapter]);

  return (
    <div>
      {resources.length > 0 && (
        <>
          <h2>Additional Resources</h2>
          <div>
            {resources.map((resource) => {
              return (
                <div key={resource.id}>
                  <Link to={`/library/resources/${resource.slug}`}>
                    <h4>{decodeHtml(resource.title.rendered)}</h4>
                    <div
                      dangerouslySetInnerHTML={{
                        __html: resource.excerpt.rendered,
                      }}
                    ></div>
                  </Link>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
};

let DownloadButtons = () => {
  let params = useParams();
  let [guidebookSlug, setGuidebookSlug] = useState(null);

  useEffect(() => {
    if (params.guidebookSlug) {
      setGuidebookSlug(params.guidebookSlug);
    }
  }, []);

  return (
    <div className="flex justify-center items-center text-bold mt-2 mb-6 ">
      <a
        tabIndex={1}
        className="download-button flex items-center mr-2 px-6 py-2 !border-2 border-white text-white"
        onClick={() => downloadGuidebookEpub(guidebookSlug)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            downloadGuidebookEpub(guidebookSlug);
          }
        }}
      >
        <Download className="text-white h-5 w-5 mr-2" />
        <span>Epub</span>
      </a>

      <a
        tabIndex={1}
        className="download-button flex items-center mr-2 px-6 py-2 !border-2 border-white text-white"
        onClick={() => downloadGuidebookPdf(guidebookSlug)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            downloadGuidebookPdf(guidebookSlug);
          }
        }}
      >
        <Download className="text-white h-5 w-5 mr-2" />
        <span>PDF</span>
      </a>
    </div>
  );
};

let GuidebookChapterBreadcrumbs = ({ firstChapterTitle, slug }) => {
  return (
    <div className="guidebook-breadcrumbs text-sm">
      <Link reloadDocument to="/library">
        Library
      </Link>
      <span className="mx-2">/</span>
      <Link reloadDocument to="/library/guidebooks">
        Guidebooks
      </Link>
      <span className="mx-2">/</span>
      <Link reloadDocument to={`/library/guidebooks/${slug}`}>
        {decodeHtml(firstChapterTitle)}
      </Link>
    </div>
  );
};

export default GuidebookChapter;
